import React, { useState, useEffect } from 'react';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import type { PluginConfigModalProps } from '../_infrastructure/pluginTypes';

export function NewsConfigModal({ isOpen, onClose, config, onSave }: PluginConfigModalProps) {
  const [keywords, setKeywords] = useState(config.keywords || 'technology,business,bitcoin');
  const [tickers, setTickers] = useState(config.tickers || 'BTC,ETH,SOL');
  const [showMarketBar, setShowMarketBar] = useState(config.showMarketBar !== 'false');
  // Default to Guardian (uses free 'test' API key on backend)
  const [newsSource, setNewsSource] = useState(config.newsSource || 'guardian');
  const [guardianApiKey, setGuardianApiKey] = useState(config.guardianApiKey || '');

  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);

  useEffect(() => {
    setKeywords(config.keywords || 'technology,business,bitcoin');
    setTickers(config.tickers || 'BTC,ETH,SOL');
    setShowMarketBar(config.showMarketBar !== 'false');
    setNewsSource(config.newsSource || 'guardian');
    setGuardianApiKey(config.guardianApiKey || '');
  }, [config, isOpen]);

  const handleSave = () => {
    onSave({
      keywords: keywords.trim(),
      tickers: tickers.trim(),
      showMarketBar: showMarketBar ? 'true' : 'false',
      newsSource,
      guardianApiKey: guardianApiKey.trim(),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
      }}
      onMouseDown={handleOverlayMouseDown}

      onClick={handleOverlayClick}
    >
      <div
        ref={modalContentRef}
        className="xv-scroll"
        style={{
          background: 'var(--color-surface)',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          width: '400px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text)' }}>News Settings</span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--color-text-muted)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-subtle)', marginBottom: '6px' }}>
              News Source
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="newsSource"
                  value="guardian"
                  checked={newsSource === 'guardian'}
                  onChange={() => setNewsSource('guardian')}
                  style={{ accentColor: 'var(--color-primary)' }}
                />
                The Guardian (full text)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="newsSource"
                  value="google"
                  checked={newsSource === 'google'}
                  onChange={() => setNewsSource('google')}
                  style={{ accentColor: 'var(--color-primary)' }}
                />
                Google News
              </label>
            </div>
          </div>

          {newsSource === 'guardian' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-subtle)', marginBottom: '6px' }}>
                Guardian API Key (optional)
              </label>
              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '0 0 8px 0' }}>
                Leave blank to use free tier. For higher limits, get a key at <a href="https://open-platform.theguardian.com/access/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>open-platform.theguardian.com</a>
              </p>
              <input
                type="password"
                value={guardianApiKey}
                onChange={(e) => setGuardianApiKey(e.target.value)}
                placeholder="Optional - uses free tier by default"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-subtle)', marginBottom: '6px' }}>
              Keywords
            </label>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '0 0 8px 0' }}>
              Crypto: bitcoin, ethereum, defi | General: technology, business, politics
            </p>
            <textarea
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="technology, business, bitcoin, ethereum"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border-strong)',
                borderRadius: '6px',
                fontSize: '13px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-subtle)', marginBottom: '6px' }}>
              Market Tickers
            </label>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '0 0 8px 0' }}>
              Crypto: BTC, ETH, SOL | Stocks: AAPL, NVDA, TSLA, C
            </p>
            <input
              type="text"
              value={tickers}
              onChange={(e) => setTickers(e.target.value)}
              placeholder="BTC, ETH, SOL"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border-strong)',
                borderRadius: '6px',
                fontSize: '13px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--color-text-subtle)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showMarketBar}
                onChange={(e) => setShowMarketBar(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary)', cursor: 'pointer' }}
              />
              Show market ticker bar
            </label>
          </div>
        </div>

        <div style={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          background: 'var(--color-surface-muted)',
          borderRadius: '0 0 12px 12px',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-strong)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              color: 'var(--color-text-subtle)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              background: 'var(--color-primary)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              color: 'white',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
